/**
 * NewIterationModal Component
 *
 * Modal dialog for creating a new iteration.
 * Users can enter a prompt describing what changes/refinements they want.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import type { TaskIteration } from '@/types/tasks';
import { formatFileSize } from '@/types/tasks';
import { createIteration, submitIteration, uploadIterationAttachment, copyDiskFileToTask } from '@/services/api';
import DiskFilePickerModal from '@/components/tasks/DiskFilePickerModal';

interface PendingFile {
  file: File;
  uploading: boolean;
  error?: string;
}

interface NewIterationModalProps {
  taskId: string;
  isOpen: boolean;
  onClose: () => void;
  onCreated: (iteration: TaskIteration) => void;
  initialPrompt?: string;
}

export default function NewIterationModal({
  taskId,
  isOpen,
  onClose,
  onCreated,
  initialPrompt,
}: NewIterationModalProps) {
  const { t } = useTranslation('tasks');
  const [userPrompt, setUserPrompt] = useState(initialPrompt || '');

  // Sync initialPrompt when modal opens with pre-filled text
  useEffect(() => {
    if (isOpen && initialPrompt) {
      setUserPrompt(initialPrompt);
    }
  }, [isOpen, initialPrompt]);

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [showDiskPicker, setShowDiskPicker] = useState(false);
  const [pendingDiskFiles, setPendingDiskFiles] = useState<{ id: string; name: string }[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map(file => ({
      file,
      uploading: false,
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Create the iteration
      const iteration = await createIteration(taskId, {
        user_prompt: userPrompt.trim() || undefined,
      });

      // Upload any pending files
      for (let i = 0; i < pendingFiles.length; i++) {
        const pf = pendingFiles[i];
        setPendingFiles(prev =>
          prev.map((f, idx) => idx === i ? { ...f, uploading: true } : f)
        );

        try {
          await uploadIterationAttachment(taskId, iteration.id, pf.file, 'input');
        } catch (uploadErr) {
          console.error('Failed to upload file:', uploadErr);
          setPendingFiles(prev =>
            prev.map((f, idx) =>
              idx === i ? { ...f, uploading: false, error: 'Upload failed' } : f
            )
          );
        }
      }

      // Copy disk files to iteration
      for (const df of pendingDiskFiles) {
        try {
          await copyDiskFileToTask(taskId, df.id, iteration.id);
        } catch (err) {
          console.error('Failed to copy disk file:', df.name, err);
        }
      }

      // Submit the iteration (draft → pending, triggers upgrade_to_full)
      const submittedIteration = await submitIteration(taskId, iteration.id);

      // Reset form
      setUserPrompt('');
      setPendingFiles([]);
      setPendingDiskFiles([]);
      onCreated(submittedIteration);
    } catch (err) {
      console.error('Failed to create iteration:', err);
      setError(err instanceof Error ? err.message : 'Failed to create iteration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    setUserPrompt('');
    setPendingFiles([]);
    setPendingDiskFiles([]);
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg z-10">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('newIteration.title')}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {t('newIteration.subtitle')}
              </p>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-4">
              {/* Error */}
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Prompt Input */}
              <div>
                <label
                  htmlFor="user-prompt"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t('newIteration.requestLabel')}
                </label>
                <textarea
                  ref={textareaRef}
                  id="user-prompt"
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder={t('newIteration.requestPlaceholder')}
                  disabled={isSubmitting}
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('newIteration.inputFilesLabel')}
                </label>
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input {...getInputProps()} disabled={isSubmitting} />
                  <svg
                    className="mx-auto h-8 w-8 text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="mt-1 text-sm text-gray-600">
                    {isDragActive
                      ? t('newIteration.dropActive')
                      : t('newIteration.dropInactive')}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{t('newIteration.maxSize')}</p>
                </div>

                {/* Attach from Disk button */}
                <button
                  type="button"
                  onClick={() => setShowDiskPicker(true)}
                  disabled={isSubmitting}
                  className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1 disabled:opacity-50"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                  </svg>
                  {t('tasks:detail.attachFromDisk', 'Attach from Disk')}
                </button>

                {/* Pending Files List */}
                {pendingFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {pendingFiles.map((pf, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-gray-400">📎</span>
                          <span className="text-sm text-gray-700 truncate">
                            {pf.file.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {formatFileSize(pf.file.size)}
                          </span>
                          {pf.uploading && (
                            <span className="text-xs text-blue-600">{t('newIteration.uploading')}</span>
                          )}
                          {pf.error && (
                            <span className="text-xs text-red-600">{pf.error}</span>
                          )}
                        </div>
                        {!pf.uploading && (
                          <button
                            type="button"
                            onClick={() => removePendingFile(index)}
                            className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Pending Disk Files List */}
                {pendingDiskFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {pendingDiskFiles.map((df) => (
                      <div key={df.id} className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg text-sm">
                        <svg className="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101" />
                        </svg>
                        <span className="flex-1 truncate text-gray-700">{df.name}</span>
                        <button
                          type="button"
                          onClick={() => setPendingDiskFiles(prev => prev.filter(f => f.id !== df.id))}
                          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3 sticky bottom-0 rounded-b-lg">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                {t('newIteration.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    {t('newIteration.requesting')}
                  </>
                ) : (
                  t('newIteration.submit')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <DiskFilePickerModal
        isOpen={showDiskPicker}
        taskId={taskId}
        onClose={() => setShowDiskPicker(false)}
        onFilesCopied={async (selectedFiles) => {
          setPendingDiskFiles(prev => {
            const existingIds = new Set(prev.map(f => f.id));
            const newFiles = selectedFiles.filter(f => !existingIds.has(f.id));
            return [...prev, ...newFiles];
          });
        }}
      />
    </div>
  );
}
